import { useEffect, useState } from "react"
import { PropsSingle } from "react-day-picker"
import { useTranslation } from "react-i18next"
import { getYear, parse, setYear } from "date-fns"
import { Calendar, CalendarFooter } from "@/components/ui/calendar"
import {
  DateInput,
  DatePickerFooter,
  validDateRegex,
  validDateWhileTypingRegex,
  VISUAL_DATE_FORMAT,
  VISUAL_DATE_SEPARATOR,
} from "@/components/ui/date-picker"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { YearPicker } from "@/components/ui/year-picker"
import { formatDate } from "@/lib/format-date"

type Props = {
  onSelect?: (date?: Date) => void
  defaultValue?: Date
  error?: string
  setError?: (message: string) => void
  onClear: () => void
  disabled?: boolean
  id?: string
}

const today = new Date()

const validateDateFormat = (input: string) => {
  return validDateRegex.test(input)
}

const BirthDatePicker = ({ defaultValue, onSelect, error, setError, onClear, disabled, id }: Props) => {
  const [initialDataFlag, setInitialDataFlag] = useState(false)
  const [fieldValue, setFieldValue] = useState<string | undefined>()
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(defaultValue)
  const [isFocused, setIsFocused] = useState(false)
  const [open, setOpen] = useState(false)

  const { t } = useTranslation()

  useEffect(() => {
    if (defaultValue && !initialDataFlag) {
      setSelectedDate(defaultValue)
      setFieldValue(formatDate(defaultValue, VISUAL_DATE_FORMAT))
      setInitialDataFlag(true)
    }
  }, [initialDataFlag, defaultValue])

  const handleSelect: PropsSingle["onSelect"] = (date, selectedDay) => {
    if (formatDate(selectedDate) === formatDate(selectedDay)) return

    setSelectedDate(date)
    onSelect?.(date)
    setFieldValue(formatDate(date, VISUAL_DATE_FORMAT))

    handleOpenChange(false)
  }

  const handleReset = () => {
    setSelectedDate(undefined)
    setFieldValue("")
    onSelect?.(undefined)
    onClear()
  }

  const handleOpenChange = (state: boolean) => {
    setIsFocused(state)
    setOpen(state)
  }

  const keyPressFunc = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      const val = fieldValue
      if (val && (val.length === 3 || val.length === 6)) {
        setFieldValue(val.slice(0, val.length - 1))
      }
    }
  }

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    let val = evt.target.value
    if (val && !validDateWhileTypingRegex.test(val)) {
      evt.preventDefault()
      return
    }

    if (val.length === 2 || val.length === 5) {
      val += VISUAL_DATE_SEPARATOR
    }
    setFieldValue(val)
    validateDate(val)
  }

  const handleInputFieldClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    setOpen(true)
    setIsFocused(true)
  }

  const handleCalendarIconClick = (e: React.SyntheticEvent<HTMLElement, Event>) => {
    e.preventDefault()
    setOpen((open) => !open)
    setIsFocused(true)
  }

  const validateDate = (input: string) => {
    setIsFocused(input !== "")
    if (!validateDateFormat(input)) {
      setSelectedDate(undefined)
      setError?.("common.fieldErrorList.invalidDob")
      onSelect?.(undefined)
      return
    }
    const parsedDate = parse(input ?? "", VISUAL_DATE_FORMAT, new Date())
    if (Number.isNaN(parsedDate.getTime())) {
      setError?.("common.fieldErrorList.invalidDob")
      return
    }
    if (formatDate(selectedDate) === formatDate(parsedDate)) {
      setError?.("")
      return
    }
    setError?.("")
    setSelectedDate(parsedDate)
    onSelect?.(parsedDate)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild onClick={handleInputFieldClick}>
        <DateInput
          data-testid="date-of-birth-date-picker"
          label={`${t("common.dateOfBirth")}*`}
          type="text"
          focus={isFocused}
          error={error}
          translateError
          readOnly={false}
          onChange={handleChange}
          onKeyDown={keyPressFunc}
          onCalendarIconClick={handleCalendarIconClick}
          value={fieldValue ?? ""}
          disabled={disabled}
          id={id}
        />
      </PopoverTrigger>

      <PopoverContent
        sideOffset={0}
        className="bg-surface pointer-events-auto w-auto rounded-lg p-0"
        align="start"
        onOpenAutoFocus={(e: Event) => e.preventDefault()}
        onCloseAutoFocus={(e: Event) => e.preventDefault()}>
        <Tabs defaultValue="exact">
          <TabsList className="mx-4 my-2" variant="secondary">
            <TabsTrigger data-testid="exact" variant="secondary" value="exact" asChild>
              Date
            </TabsTrigger>
            <TabsTrigger data-testid="approximate" variant="secondary" value="approximate" asChild>
              Year
            </TabsTrigger>
          </TabsList>
          <TabsContent className="m-0 p-0" value="exact">
            <Calendar
              fixedWeeks
              disabled={{ after: today }}
              onSelect={handleSelect}
              selected={selectedDate}
              today={selectedDate}
              month={selectedDate}
              onMonthChange={setSelectedDate}
              className="pb-2"
              mode="single"
              components={{
                Footer: () => (
                  <DatePickerFooter>
                    <CalendarFooter disabled={!selectedDate} onClick={handleReset} />
                  </DatePickerFooter>
                ),
              }}
            />
          </TabsContent>
          <TabsContent className="m-0 p-0" value="approximate">
            <YearPicker
              defaultSelected={getYear(selectedDate || today)}
              fromYear={getYear(selectedDate || today)}
              onSelect={(year) => setSelectedDate((date) => setYear(date || today, year))}
              onReset={handleReset}
            />
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  )
}

export { BirthDatePicker }
